import { useState } from "react";
import { Play } from "lucide-react";

/**
 * LazyYouTube — facade pattern.
 * Shows a poster thumbnail + play button until the user clicks. Only mounts
 * the actual YouTube iframe AFTER the first click. This cuts initial-page
 * memory by ~75% on pages with multiple embedded players, which prevents
 * iOS Safari from killing the tab with "A problem repeatedly occurred."
 *
 * Props:
 *   videoId    — required YouTube video ID
 *   playlistId — optional playlist to attach (`list=…`)
 *   title      — accessible title
 *   testId     — root data-testid (defaults to "lazy-youtube")
 *   posterRes  — "hqdefault" (default, 480x360 — fast) or "maxresdefault" (1280x720 — sharper but bigger)
 */
export const LazyYouTube = ({
    videoId,
    playlistId,
    title = "Video",
    testId = "lazy-youtube",
    posterRes = "hqdefault",
    className = "",
}) => {
    const [active, setActive] = useState(false);
    const poster = `https://i.ytimg.com/vi/${videoId}/${posterRes}.jpg`;
    const src =
        `https://www.youtube.com/embed/${videoId}` +
        `?autoplay=1&rel=0&modestbranding=1` +
        (playlistId ? `&list=${playlistId}` : "");

    if (active) {
        return (
            <iframe
                key={videoId}
                data-testid={`${testId}-iframe`}
                className={`absolute inset-0 w-full h-full ${className}`}
                src={src}
                title={title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        );
    }

    return (
        <button
            type="button"
            onClick={() => setActive(true)}
            data-testid={`${testId}-poster`}
            aria-label={`Play ${title}`}
            className={`group absolute inset-0 w-full h-full block bg-black overflow-hidden ${className}`}
        >
            <img
                src={poster}
                alt={title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
            <span className="absolute inset-0 grid place-items-center">
                <span className="h-16 w-16 sm:h-20 sm:w-20 grid place-items-center bg-[#D4AF37] group-hover:scale-110 transition-transform shadow-[0_0_40px_rgba(212,175,55,0.5)]">
                    <Play size={28} className="text-black ml-1 fill-black" />
                </span>
            </span>
        </button>
    );
};
